import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Calendar as CalendarIcon,
  Trash2,
  Edit2,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { ContentTag } from "@/components/ui/content-tag";
import { getTemplateInfo, getLengthTagInfo } from "@/lib/contentHelpers";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useWebsites } from "@/context/WebsitesContext";
import { supabase } from "@/integrations/supabase/client";
import { useAppState } from "@/context/AppStateContext";
import { useNavigate } from "react-router-dom";

export type CalendarItem = {
  id: string;
  title: string;
  digest?: string;
  fullContent?: string;
  isGenerating?: boolean;
  isGenerated?: boolean;
  postThemeId?: string; // Database ID for post_themes table
  format_template?: string;
  length_tag?: string;
  tone_style?: any;
  keywords?: string[];
  section_layout?: string[];
  element_settings?: Record<string, any>;
  content_type?: string;
  scheduled_at?: string | null;
};

const PlannedContent = () => {
  const { currentWebsite } = useWebsites();
  const { setCurrentRightPaneUrl } = useAppState();
  const navigate = useNavigate();
  const [calendarItems, setCalendarItems] = useState<CalendarItem[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch calendar items from database
  const fetchCalendarItems = async () => {
    if (!currentWebsite?.id) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('post_themes')
        .select('*')
        .eq('website_id', currentWebsite.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching calendar items:', error);
        setError('Failed to load planned content');
        return;
      }

      console.log('PlannedContent: Raw database data:', data);
      console.log('PlannedContent: Data count:', data.length);
      
      // Transform database items to CalendarItem format
      const transformedItems: CalendarItem[] = data.map(item => ({
        id: item.id,
        title: item.subject_matter,
        digest: item.post_digest,
        fullContent: item.post_content,
        isGenerated: !!item.post_content,
        // Show generating state only when status is explicitly generating AND no content exists yet
        isGenerating: (item.status === 'generatingidea' || item.status === 'pending') && !item.post_content,
        format_template: item.format_template || 'guide',
        length_tag: item.length_tag || 'long',
        content_type: item.content_type || 'post',
        keywords: item.keywords || [],
        tone_style: item.tone_style || { tone: 'neutral' },
        section_layout: item.section_layout || [],
        element_settings: item.element_settings || {},
        scheduled_at: item.scheduled_date
      }));

      console.log('PlannedContent: Transformed items:', transformedItems);
      console.log('PlannedContent: Items count:', transformedItems.length);
      setCalendarItems(transformedItems);
    } catch (err) {
      console.error('Error fetching calendar items:', err);
      setError('Failed to load planned content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarItems();
  }, [currentWebsite?.id]);

  // Listen for localStorage calendar changes from chat
  useEffect(() => {
    const handleCalendarChange = () => {
      console.log('PlannedContent: Calendar change detected, refreshing data');
      fetchCalendarItems();
    };

    window.addEventListener('calendar-enabled-changed', handleCalendarChange);
    
    return () => {
      window.removeEventListener('calendar-enabled-changed', handleCalendarChange);
    };
  }, []);

  // Set up real-time subscription for post_themes changes
  useEffect(() => {
    if (!currentWebsite?.id) return;

    console.log('PlannedContent: Setting up real-time subscription for post_themes');
    const postThemesChannel = supabase
      .channel(`planned_content_${currentWebsite.id}`)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'post_themes',
          filter: `website_id=eq.${currentWebsite.id}`
        }, 
        (payload) => {
          console.log('PlannedContent: post_themes table changed', payload);
          // Refresh the calendar items when changes occur
          fetchCalendarItems();
        }
      )
      .subscribe((status) => {
        console.log(`PlannedContent real-time subscription status: ${status}`);
      });

    return () => {
      console.log('PlannedContent: Cleaning up real-time subscription');
      postThemesChannel.unsubscribe();
    };
  }, [currentWebsite?.id]);

  // Group items by month
  const itemsByMonth = useMemo(() => {
    const grouped: { [key: string]: CalendarItem[] } = {};
    
    calendarItems.forEach(item => {
      if (item.scheduled_at) {
        const monthKey = format(new Date(item.scheduled_at), 'yyyy-MM');
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(item);
      } else {
        // Handle unscheduled items
        if (!grouped['unscheduled']) {
          grouped['unscheduled'] = [];
        }
        grouped['unscheduled'].push(item);
      }
    });

    // Sort items within each month by date (newest first)
    Object.keys(grouped).forEach(monthKey => {
      grouped[monthKey].sort((a, b) => {
        const ta = a.scheduled_at ? Date.parse(a.scheduled_at) : 0;
        const tb = b.scheduled_at ? Date.parse(b.scheduled_at) : 0;
        return tb - ta;
      });
    });

    return grouped;
  }, [calendarItems]);

  // Get filtered items based on selected month
  const filteredItems = useMemo(() => {
    console.log('PlannedContent: Filtering items', { 
      calendarItemsCount: calendarItems.length, 
      selectedMonth,
      itemsByMonthKeys: Object.keys(itemsByMonth)
    });
    
    if (!selectedMonth) {
      const sorted = calendarItems.sort((a, b) => {
        const ta = a.scheduled_at ? Date.parse(a.scheduled_at) : 0;
        const tb = b.scheduled_at ? Date.parse(b.scheduled_at) : 0;
        return tb - ta;
      });
      console.log('PlannedContent: Sorted items (no month selected):', sorted);
      return sorted;
    }
    const monthItems = itemsByMonth[selectedMonth] || [];
    console.log('PlannedContent: Month items:', monthItems);
    return monthItems;
  }, [calendarItems, itemsByMonth, selectedMonth]);

  // Get available months
  const availableMonths = useMemo(() => {
    return Object.keys(itemsByMonth).filter(key => key !== 'unscheduled').sort().reverse();
  }, [itemsByMonth]);

  const handleDateSelect = async (item: CalendarItem, date: Date | undefined) => {
    if (date) {
      try {
        const { error } = await supabase
          .from('post_themes')
          .update({ scheduled_date: date.toISOString() })
          .eq('id', item.id);

        if (error) {
          console.error('Error updating scheduled date:', error);
        } else {
          // Update local state
          setCalendarItems(prev => 
            prev.map(calendarItem => 
              calendarItem.id === item.id 
                ? { ...calendarItem, scheduled_at: date.toISOString() }
                : calendarItem
            )
          );
        }
      } catch (error) {
        console.error('Error updating scheduled date:', error);
      }
    }
  };

  const handleRemove = async (id: string) => {
    try {
      const { error } = await supabase
        .from('post_themes')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error removing item:', error);
      } else {
        // Update local state
        setCalendarItems(prev => prev.filter(item => item.id !== id));
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const handleEdit = (item: CalendarItem) => {
    console.log('handleEdit called with item:', {
      id: item.id,
      title: item.title,
      isGenerated: item.isGenerated,
      hasFullContent: !!item.fullContent,
      fullContentLength: item.fullContent?.length
    });
    
    if (item.isGenerated && item.fullContent) {
      console.log('Edit item:', item);
      // Set the right pane URL and navigate to dashboard for shared layout
      console.log('Setting right pane URL and navigating to dashboard:', `/content-editor/${item.id}`);
      setCurrentRightPaneUrl(`/content-editor/${item.id}`);
      navigate('/dashboard');
    } else {
      console.log('Item not generated yet, not opening editor');
    }
  };

  const handleRegenerate = async (item: CalendarItem) => {
    if (!item.id || !currentWebsite?.id) {
      console.error('Missing item ID or website ID');
      return;
    }

    try {
      console.log('Starting content regeneration for:', item.id);
      // 1) Ensure AI tags and sections are set before starting V4
      let format_template = item.format_template || 'guide';
      let length_tag = item.length_tag || 'medium';
      let tone_style: any = item.tone_style || { tone: 'neutral' };
      let keywords: string[] = item.keywords || [];
      let section_layout: string[] = Array.isArray(item.section_layout) ? item.section_layout : [];
      
      // Get session for auth
      const { data: sessionData } = await supabase.auth.getSession();

      try {
        // Suggest tags (format, length, tone, keywords)
        const suggestResp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/suggest-post-format`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            title: item.title,
            website_url: currentWebsite.url || '',
            website_id: currentWebsite.id,
            language: 'en',
            keywords
          })
        });
        if (suggestResp.ok) {
          const json = await suggestResp.json();
          if (json.success && json.suggestion) {
            format_template = json.suggestion.format_template || format_template;
            length_tag = json.suggestion.length_tag || length_tag;
            tone_style = { tone: json.suggestion.tone_style || (tone_style?.tone || 'neutral') };
            keywords = Array.isArray(json.suggestion.keywords) ? json.suggestion.keywords : keywords;
            
            // Save category associations if provided
            const suggestedCategories = json.suggestion.categories || [];
            if (suggestedCategories.length > 0 && item.id) {
              console.log('PlannedContent: Saving category associations:', suggestedCategories);
              
              // First, clear existing category associations
              await supabase
                .from('post_theme_categories')
                .delete()
                .eq('post_theme_id', item.id);
              
              // Insert new category associations
              for (const categoryId of suggestedCategories) {
                const { error: categoryError } = await supabase
                  .from('post_theme_categories')
                  .insert({
                    post_theme_id: item.id,
                    wordpress_category_id: categoryId
                  });
                
                if (categoryError) {
                  console.error(`PlannedContent: Error creating category association for ${categoryId}:`, categoryError);
                } else {
                  console.log(`PlannedContent: Successfully associated category ${categoryId}`);
                }
              }
            }
          }
        }
        // Ensure section layout
        const { data: sectionTypes } = await supabase
          .from('section_types')
          .select('id');
        if (Array.isArray(sectionTypes) && sectionTypes.length > 0) {
          const excluded = new Set(['quote', 'image', 'testimonial']);
          const validated = sectionTypes
            .map((s: any) => s.id)
            .filter((id: string) => !excluded.has(id));
          if (validated.length > 0) section_layout = validated;
        }
        // Persist to DB
        const { error: updErr } = await supabase
          .from('post_themes')
          .update({
            format_template,
            length_tag,
            tone_style,
            keywords,
            section_layout
          })
          .eq('id', item.id);
        if (updErr) console.warn('PlannedContent: Failed to persist AI tags/sections before SSE', updErr);
      } catch (e) {
        console.warn('PlannedContent: AI tagging/sections pre-step failed, proceeding with fallbacks', e);
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-content-v4`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(sessionData?.session?.access_token ? { 'Authorization': `Bearer ${sessionData.session.access_token}` } : {}),
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          title: item.title,
          website_id: currentWebsite.id,
          is_onboarding: false,
          website_url: currentWebsite.url || '',
          format_template,
          length_tag,
          tone_style: JSON.stringify(tone_style || { tone: 'neutral' }),
          content_type: item.content_type || 'post',
          keywords,
          section_layout,
          element_settings: item.element_settings || {},
          language: 'en',
          cornerstone_content: [],
          scraped_content: [],
          postThemeId: item.id,
          passedWebsiteId: currentWebsite.id
        })
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('generate-content-v4 error:', text);
        throw new Error('Failed to start generation');
      }

      console.log('Content generation started successfully');
      // The real-time subscription will automatically update the UI when content is generated
    } catch (error) {
      console.error('Error regenerating content:', error);
    }
  };

  const formatMonthDisplay = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return format(date, 'MMM yyyy');
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Month Filter - match chat header sizing */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={selectedMonth === null ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setSelectedMonth(null)}
            className="h-8"
          >
            All posts
          </Button>
          <div className="flex items-center gap-1">
            {selectedMonth && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  const idx = availableMonths.indexOf(selectedMonth);
                  const next = idx >= 0 ? availableMonths[idx + 1] : undefined;
                  if (next) setSelectedMonth(next);
                }}
                disabled={(() => {
                  if (!selectedMonth) return true;
                  const idx = availableMonths.indexOf(selectedMonth);
                  return idx < 0 || idx + 1 >= availableMonths.length;
                })()}
                title="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <Select
              value={selectedMonth ?? undefined}
              onValueChange={(v) => setSelectedMonth(v)}
            >
              <SelectTrigger className="h-8 w-[180px] bg-background border-border hover:bg-accent hover:text-accent-foreground">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="bg-background">
                {availableMonths.map((monthKey) => (
                  <SelectItem key={monthKey} value={monthKey}>
                    {formatMonthDisplay(monthKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMonth && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  const idx = availableMonths.indexOf(selectedMonth);
                  const prev = idx > 0 ? availableMonths[idx - 1] : undefined;
                  if (prev) setSelectedMonth(prev);
                }}
                disabled={(() => {
                  if (!selectedMonth) return true;
                  const idx = availableMonths.indexOf(selectedMonth);
                  return idx <= 0;
                })()}
                title="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">Loading planned content...</h3>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-medium text-destructive mb-2">Error loading content</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">
                  {selectedMonth ? `No items for ${formatMonthDisplay(selectedMonth)}` : 'No planned content yet'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedMonth ? 'Try selecting a different month or add new content from the chat.' : 'Add content ideas from the chat to see them here.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredItems.map((item) => (
                  <Card 
                    key={item.id} 
                    className={cn(
                      "transition-shadow border border-gray-200",
                      item.isGenerated && item.fullContent && "cursor-pointer hover:shadow-md"
                    )}
                    onClick={() => {
                      if (item.isGenerated && item.fullContent) {
                        handleEdit(item);
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={cn(
                                    "justify-start text-left font-normal h-7 px-2 text-xs",
                                    !item.scheduled_at && "text-muted-foreground"
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CalendarIcon className="mr-1 h-3 w-3" />
                                  {item.scheduled_at ? format(new Date(item.scheduled_at), 'MMM d') : <span>Pick date</span>}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={item.scheduled_at ? new Date(item.scheduled_at) : undefined}
                                  onSelect={(date) => handleDateSelect(item, date)}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            {item.isGenerated && item.fullContent && (
                              <div className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="h-3 w-3" />
                                <span className="text-xs font-medium">Ready</span>
                              </div>
                            )}
                          </div>
                          
                          <h3 className="text-base font-medium leading-tight mb-2 line-clamp-2">{item.title}</h3>
                          
                          {item.isGenerating && (
                            <div className="flex items-center gap-2 mb-2">
                              <div className="animate-spin h-3 w-3 border border-blue-600 border-t-transparent rounded-full"></div>
                              <p className="text-xs text-blue-700">Writing article...</p>
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-1">
                            {item.format_template && (
                              <ContentTag
                                type="category"
                                label={item.format_template}
                                description={getTemplateInfo(item.format_template).description}
                              />
                            )}
                            {item.length_tag && (
                              <ContentTag
                                type="length"
                                label={item.length_tag}
                                description={getLengthTagInfo(item.length_tag).description}
                              />
                            )}
                            {item.content_type && item.content_type !== 'post' && (
                              <ContentTag
                                type="category"
                                label={item.content_type}
                              />
                            )}
                            {item.keywords && item.keywords.length > 0 && (
                              <ContentTag
                                type="keyword"
                                label={item.keywords.slice(0, 2).join(', ')}
                                description={item.keywords.length > 2 ? `+${item.keywords.length - 2} more` : undefined}
                              />
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1 ml-4">
                          {item.isGenerated && item.fullContent ? (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(item);
                              }} 
                              title="Edit"
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRegenerate(item);
                              }} 
                              title="Generate article"
                              disabled={item.isGenerating}
                            >
                              <RefreshCw className={cn("h-3 w-3", item.isGenerating && "animate-spin")} />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(item.id);
                            }} 
                            title="Remove"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default PlannedContent;
